import { useState, useCallback, useEffect } from 'react';
import { api } from '@/lib/api';
import { toast } from '@/components/ui/use-toast';
import type {
  CalendarShareDetail,
  CalendarShareInput,
  CalendarShareUpdate,
  SharedCalendarSummary,
} from '../types';

interface UseCalendarSharingOptions {
  calendarId: string | null;
}

export function useCalendarSharing({ calendarId }: UseCalendarSharingOptions) {
  const [shares, setShares] = useState<CalendarShareDetail[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchShares = useCallback(async () => {
    if (!calendarId) return;
    setLoading(true);
    try {
      const { shares: data } = await api.listCalendarShares(calendarId);
      setShares(data);
    } catch (err) {
      console.error('[calendar sharing] Failed to fetch shares', err);
      toast({
        title: 'Failed to load shares',
        description: err instanceof Error ? err.message : 'Could not load calendar shares',
        variant: 'destructive',
      });
      setShares([]);
    } finally {
      setLoading(false);
    }
  }, [calendarId]);

  useEffect(() => {
    fetchShares();
  }, [fetchShares]);

  const createShare = useCallback(
    async (input: CalendarShareInput) => {
      if (!calendarId) throw new Error('No calendar selected');
      const share = await api.createCalendarShare(calendarId, input);
      setShares((prev) => [...prev, share]);
      return share;
    },
    [calendarId],
  );

  const updateShare = useCallback(
    async (shareId: string, input: CalendarShareUpdate) => {
      if (!calendarId) throw new Error('No calendar selected');
      const updated = await api.updateCalendarShare(calendarId, shareId, input);
      setShares((prev) => prev.map((s) => (s.id === shareId ? updated : s)));
      return updated;
    },
    [calendarId],
  );

  const deleteShare = useCallback(
    async (shareId: string) => {
      if (!calendarId) throw new Error('No calendar selected');
      await api.deleteCalendarShare(calendarId, shareId);
      setShares((prev) => prev.filter((s) => s.id !== shareId));
    },
    [calendarId],
  );

  return {
    shares,
    loading,
    fetchShares,
    createShare,
    updateShare,
    deleteShare,
  };
}

export function useSharedCalendars() {
  const [sharedCalendars, setSharedCalendars] = useState<SharedCalendarSummary[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchSharedCalendars = useCallback(async () => {
    setLoading(true);
    try {
      const { sharedCalendars: data } = await api.listSharedCalendars();
      setSharedCalendars(data);
    } catch (err) {
      console.error('[calendar sharing] Failed to fetch shared calendars', err);
      setSharedCalendars([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSharedCalendars();
  }, [fetchSharedCalendars]);

  return { sharedCalendars, loading, fetchSharedCalendars };
}
